import { useQuery } from '@tanstack/react-query';
import { programByIdQueryOptions } from '@/features/programs/program-edit/program-by-id-query';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolve a logo da empresa de uma vaga via `Job → Program → Company`.
 *
 * Reuso transversal nas 9 telas do hub de edição de vagas (hub + 8
 * subseções). Cada container chama `useJobCompanyLogo(job?.programId)`
 * depois de carregar a vaga e passa `{ companyLogoUrl, companyName }` ao
 * `.component.tsx` correspondente. O componente renderiza `<img>` quando há
 * logo; senão cai no placeholder com `RouteIcon` (paridade visual com EU-157
 * antes do wire-up de logo).
 *
 * Por que esse caminho: a `Job` no domínio carrega `programId`; o detail
 * de programa (`GET /programs/:id` → `ProgramDetailRowSchema`) já expõe
 * `company: { id, slug, name, logoUrl }`. Não precisa criar endpoint novo
 * nem extender `GET /jobs/:id`.
 */
export function useJobCompanyLogo(programId: string | null | undefined): {
  companyLogoUrl: string | null;
  companyName: string | null;
} {
  const isValidUuid = typeof programId === 'string' && UUID_RE.test(programId);
  const programQuery = useQuery({
    ...programByIdQueryOptions(programId ?? ''),
    enabled: isValidUuid,
  });
  return {
    companyLogoUrl: programQuery.data?.company?.logoUrl ?? null,
    companyName: programQuery.data?.company?.name ?? null,
  };
}
